// ** Utils
import { getSubdomain } from '@/utils/domain/regions';

// ** Types
import type { AppContext } from '@/types/contextTypes';
import type { UploadFile } from '@/types/graphql';

export type GetStrapiImageUrlParams = {
	host: AppContext['host'];
	imageData?:
		| UploadFile
		| {
				backgroundLightImage?: UploadFile;
				backgroundDarkImage?: UploadFile;
		  }
		| any
		| null;
	isMobile: boolean;
	themeMode?: 'light' | 'dark';
};

// CMS source definitions - Full mapping maintained
export const cmsSource: Record<string, string> = {
	default: 'https://cms.admin.tischler.studio',
	www: 'https://cms.admin.tischler.studio',
	beta: 'https://cms.beta.tischler.studio',
	nrw: 'https://cms.tischler.nrw',
	hh: 'https://cms.hh.tischler.studio',
	'nord-hh': 'https://cms.nord-hh.tischler.studio',
	ni: 'https://cms.ni.tischler.studio',
	dus: 'https://cms.tischlerinnung-duesseldorf.de',
	bw: 'https://cms.bw.tischler.studio',
	'beta-bw': 'https://cms.beta-bw.tischler.studio',
	'beta-dus': 'https://cms.beta-dus.tischler.studio',
	'beta-nrw': 'https://cms.beta-nrw.tischler.studio',
	bund: 'https://cms.bund.tischler.studio',
	koeln: 'https://cms.koeln.tischler.studio',
	bie: 'https://cms.bie.tischler.studio',
	ruhr: 'https://cms.ruhr.tischler.studio',
	kassel: 'https://cms.kassel.tischler.studio',
	hsk: 'https://cms.hsk.tischler.studio',
	eus: 'https://cms.eus.tischler.studio',
	rheydt: 'https://cms.rheydt.tischler.studio',
	mkr: 'https://cms.mkr.tischler.studio',
	live: 'https://cms.tischler.nrw' // Live Beta
};

// Metadata for mapping specific Strapi component attributes (Restored)
export const strapiDataAttributes = {
	HomepageAttributes: 'pageTitle'
};

/**
 * Recursively flattens Strapi responses.
 */
export const getStrapiAttributes = (data: any): any => {
	if (!data) return null;
	if (typeof data !== 'object') return data;

	const attributes: Record<string, any> = {};

	Object.keys(data).forEach((key) => {
		const item = data[key];
		attributes[key] = item && typeof item === 'object' ? getStrapiAttributes(item) : item;
	});

	return attributes;
};

/**
 * Resolves the full URL for a CMS asset.
 */
export const getStrapiImageUrl = ({ host, imageData, isMobile, themeMode }: GetStrapiImageUrlParams): string => {
	const domain = getSubdomain(host);
	const baseUrl = cmsSource[domain] || cmsSource.default;

	if (!imageData) return '';

	// Case A: Themed background images
	if (themeMode && (imageData.backgroundLightImage || imageData.backgroundDarkImage)) {
		const themedImage = themeMode === 'light' ? imageData.backgroundLightImage : imageData.backgroundDarkImage;

		if (!themedImage) return '';

		const path = isMobile ? (themedImage.formats?.medium?.url ?? themedImage.url) : themedImage.url;

		return `${baseUrl}${path}`;
	}

	// Case B: Standard Strapi Image object
	if (imageData.url) {
		const path = isMobile && imageData.formats?.medium?.url ? imageData.formats.medium.url : imageData.url;

		return `${baseUrl}${path}`;
	}

	return '';
};

/**
 * Simple resolver for static files (PDFs, Docs) from the CMS.
 * Uses object destructuring for consistency with other utils.
 */
export const getStrapiFileUrl = ({ host, url }: { host: string; url: string }): string => {
	const domain = getSubdomain(host);
	const baseUrl = cmsSource[domain] || cmsSource.default;

	if (!url) return '';
	return `${baseUrl}${url}`;
};
